import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Clock, RefreshCw, Server } from "lucide-react";

import PageHeader from "../../components/PageHeader";
import { COLORS } from "../../constants";
import {
  DesenvolvedorCronHealth,
  DesenvolvedorCronJob,
  servidorOpsService,
} from "../../services/servidorOpsService";

function formatInstant(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      timeZone: "America/Sao_Paulo",
    });
  } catch {
    return iso;
  }
}

function healthLabel(health: DesenvolvedorCronHealth): string {
  switch (health) {
    case "ok":
      return "OK";
    case "stale":
      return "Atrasado";
    case "missing":
      return "Sem evidência";
    case "disabled":
      return "Desligado";
    default:
      return "Desconhecido";
  }
}

function healthClass(health: DesenvolvedorCronHealth): string {
  switch (health) {
    case "ok":
      return "bg-emerald-50 text-emerald-700";
    case "stale":
      return "bg-amber-50 text-amber-800";
    case "missing":
      return "bg-rose-50 text-rose-700";
    case "disabled":
      return "bg-neutral-100 text-neutral-500";
    default:
      return "bg-neutral-100 text-neutral-600";
  }
}

function hostLabel(host: DesenvolvedorCronJob["host"]): string {
  if (host === "api") return "API Nest";
  if (host === "db") return "Servidor de banco";
  return "Host do app";
}

const CronsServidor: React.FC = () => {
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["desenvolvedor", "crons"],
    queryFn: () => servidorOpsService.getCrons(),
    refetchInterval: 30_000,
  });

  const grouped = useMemo(() => {
    const order: Array<DesenvolvedorCronJob["host"]> = ["db", "app", "api"];
    const jobs = data?.jobs ?? [];
    return order
      .map((host) => ({
        host,
        jobs: jobs.filter((j) => j.host === host),
      }))
      .filter((g) => g.jobs.length > 0);
  }, [data?.jobs]);

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-4 md:p-6">
      <PageHeader
        title="Crons do servidor"
        description="Agenda no servidor de banco, no app e na API Nest."
        actions={
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-white disabled:opacity-60"
            style={{ background: COLORS.PRIMARY }}
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw size={16} className={isFetching ? "animate-spin" : ""} />
            Atualizar
          </button>
        }
      />

      {isLoading || !data ? (
        <p className="text-sm text-neutral-500">Carregando…</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-neutral-200 bg-white px-4 py-3 text-xs text-neutral-600 shadow-sm">
            <span className="inline-flex items-center gap-1.5">
              <Clock size={14} style={{ color: COLORS.PRIMARY }} />
              <span className="font-medium text-neutral-800">
                Fuso {data.timezone}
              </span>
            </span>
            <span>Gerado em {formatInstant(data.generatedAt)}</span>
            <span>{data.jobs.length} job(s)</span>
          </div>

          {grouped.map(({ host, jobs }) => (
            <div
              key={host}
              className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm"
            >
              <div className="mb-3 flex items-center gap-2">
                <Server size={16} style={{ color: COLORS.PRIMARY }} />
                <h2 className="text-sm font-semibold text-neutral-900">
                  {hostLabel(host)}
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-neutral-100 text-xs uppercase tracking-wide text-neutral-500">
                      <th className="pb-2 font-medium">Job</th>
                      <th className="pb-2 font-medium">Horário</th>
                      <th className="pb-2 font-medium">Cron</th>
                      <th className="pb-2 font-medium">Status</th>
                      <th className="pb-2 font-medium">Última evidência</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job) => (
                      <tr
                        key={job.id}
                        className="border-b border-neutral-50 align-top last:border-0"
                      >
                        <td className="py-3 pr-3">
                          <p className="font-medium text-neutral-900">
                            {job.name}
                          </p>
                          <p className="mt-0.5 text-xs text-neutral-500">
                            {job.description}
                          </p>
                        </td>
                        <td className="py-3 pr-3 text-xs text-neutral-700">
                          {job.schedule}
                        </td>
                        <td className="py-3 pr-3">
                          <code className="rounded bg-neutral-100 px-1.5 py-0.5 text-[11px] text-neutral-700">
                            {job.cronExpression}
                          </code>
                        </td>
                        <td className="py-3 pr-3">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${healthClass(job.health)}`}
                          >
                            {healthLabel(job.health)}
                          </span>
                        </td>
                        <td className="py-3 text-xs text-neutral-600">
                          {formatInstant(job.lastEvidenceAt)}
                          {job.lastEvidenceDetail ? (
                            <span className="mt-1 block text-[11px] text-neutral-400">
                              {job.lastEvidenceDetail}
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default CronsServidor;
